import React, { Fragment, PropsWithChildren } from 'react';
import { useDispatch } from 'react-redux';
import { ColorRepresentation, Vector3 } from 'three';

import { createRoofBasePolygonsFromSolidData, findNonBoundaryEdgesProjectedTo2D } from '@paragon/layout';

import { useAppSelector } from '../../components/hooks';
import { convertFromLayoutToDesignCoordinates } from '../../library/layout-geometry/coordinates';
import { getRoofElevations } from '../../store/selectors/layoutDocumentSelectors';
import { clearHoverItem, setTooltip } from '../../store/slices/viewportSlice';
import { Line } from '../util/Line';
import { useLayoutVerboseMode } from './verbose/layoutVerboseMode';

const IMPORTANT_POINT_COLOR = 0xff0000;

export function FiberDevTools() {
  const showDevTools = useLayoutVerboseMode();

  const roofElevations = useAppSelector(getRoofElevations);
  const roofContainers = useAppSelector((state) => state.layout.roofContainers);

  if (showDevTools === false) return null;
  if (process.env.NODE_ENV === 'production') return null;

  return (
    <>
      <ImportantPoint position={new Vector3(0, 0, 0)} tooltip="Origin" />

      {Object.values(roofContainers).map((roofContainer) => {
        const elevation = roofElevations[roofContainer.guid] ?? 0;

        const ridgeLines = roofContainer.solidData
          ? findNonBoundaryEdgesProjectedTo2D(
              roofContainer.solidData,
              createRoofBasePolygonsFromSolidData(roofContainer.solidData),
            )
          : [];

        return (
          <Fragment key={roofContainer.name}>
            <>
              {ridgeLines.map((edge) => (
                <Line
                  color={0xff0033}
                  points={[
                    convertFromLayoutToDesignCoordinates(edge.basePoint.to3D(elevation)).toArray(),
                    convertFromLayoutToDesignCoordinates(edge.endPoint.to3D(elevation)).toArray(),
                  ]}
                />
              ))}

              <mesh />
            </>
          </Fragment>
        );
      })}
    </>
  );
}

export function ImportantPoint(props: {
  color?: ColorRepresentation;
  radius?: number;
  position: Vector3;
  tooltip?: string;
}) {
  const mesh = (
    <mesh position={props.position}>
      <dodecahedronGeometry args={[props.radius ?? 2.5, 5]} />
      <meshBasicMaterial color={props.color ?? IMPORTANT_POINT_COLOR} />
    </mesh>
  );

  return props.tooltip ? <SceneTooltip tooltip={props.tooltip}>{mesh}</SceneTooltip> : mesh;
}

function SceneTooltip(props: PropsWithChildren<{ tooltip: string }>) {
  const dispatch = useDispatch();

  return (
    <group
      onPointerEnter={(event) => {
        event.stopPropagation();
        dispatch(setTooltip(props.tooltip));
      }}
      onPointerLeave={(event) => {
        event.stopPropagation();
        dispatch(clearHoverItem());
      }}
    >
      {props.children}
    </group>
  );
}
